// Helpers for the device-native bulk SMS flow — opening the agent's own Messages
// app so the text sends from THEIR personal number (not a Twilio business line).
//
// iOS and Android disagree on the sms: URI body separator:
//   iOS     →  sms:<number>&body=<text>
//   Android →  sms:<number>?body=<text>
// so we detect the platform and build the right link. Desktop has no SIM, so the
// caller should fall back (QR-to-phone) rather than attempt a native send.
//
// HOW THE HAND-OFF IS FIRED MATTERS. `window.location.href = "sms:…"` works in
// mobile Safari/Chrome but does NOTHING when the CRM is opened from the iPhone
// home screen (an installed/standalone web app) or inside an in-app browser —
// the navigation to a non-http scheme is dropped with no error. Agents running
// the CRM as a home-screen app therefore stepped through the whole queue while
// Messages never opened. Always hand off via a real link activation (an <a href>
// the agent taps, or `openSmsApp` below) instead.

export function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || navigator.vendor || "";
  // iPadOS 13+ reports as a Mac, so also sniff touch points.
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

// True when the current device can plausibly send an SMS from its own number.
export function canSendNativeSms() {
  return detectPlatform() !== "desktop";
}

// True when the CRM is running as an installed/home-screen app rather than in a
// normal browser tab. Those web views are the ones that swallow sms: navigations,
// so the UI warns and offers a copy-paste fallback there.
export function isStandaloneApp() {
  if (typeof window === "undefined") return false;
  if (window.navigator && window.navigator.standalone === true) return true; // iOS home screen
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: minimal-ui)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches
    );
  } catch (err) {
    return false;
  }
}

// Normalise an AU phone number for an sms: link. Keeps a leading + (intl), strips
// spaces/brackets/dashes. CRM phone fields are free text, so also:
//   • take the FIRST number when a field holds two ("0400 111 222 / 0400 333 444"),
//     otherwise the digits merge into an unsendable number;
//   • drop trailing extension notes ("0398881234 ext 4");
//   • repair "61412345678" → "+61412345678" and "412345678" → "0412345678"
//     (leading zero lost in a spreadsheet import).
export function normalisePhoneForSms(raw) {
  if (!raw) return "";
  let text = String(raw).trim();
  // First number only — split on separators that mean "and another number".
  text = text.split(/\s*(?:\/|,|;|\||\n|&|\bor\b)\s*/i)[0].trim();
  text = text.replace(/\b(?:ext|extn|x)\.?\s*\d+\s*$/i, "").trim();
  const hasPlus = text.startsWith("+");
  const digits = text.replace(/[^\d]/g, "");
  if (!digits) return "";
  if (hasPlus) return `+${digits}`;
  // 61 + 9 digits is an AU number written without its +.
  if (digits.length === 11 && digits.startsWith("61")) return `+${digits}`;
  // 9 digits starting with 4 is an AU mobile that lost its leading 0.
  if (digits.length === 9 && digits.startsWith("4")) return `0${digits}`;
  return digits;
}

// Build the platform-correct sms: deep link for one recipient + body.
export function buildSmsLink(phone, body, platform = detectPlatform()) {
  const number = normalisePhoneForSms(phone);
  const encodedBody = encodeURIComponent(body || "");
  // Android uses ?body=, iOS/everything-else uses &body=.
  const separator = platform === "android" ? "?" : "&";
  return `sms:${number}${separator}body=${encodedBody}`;
}

// Build a mailto: deep link for one recipient (Email channel of the Bulk
// Communicator). Unlike sms:, mailto: is honoured on desktop too, so the Email
// flow isn't gated to phones. Subject and body are URL-encoded.
export function buildMailtoLink(email, subject, body) {
  const to = encodeURIComponent(String(email || "").trim());
  const params = [];
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const query = params.length ? `?${params.join("&")}` : "";
  return `mailto:${to}${query}`;
}

/**
 * Hand off to the native Messages app as a link activation, which standalone
 * home-screen apps and in-app browsers honour (a plain location.href does not).
 * Must be called inside the agent's tap handler. Returns false only if the
 * document isn't available.
 */
export function openSmsApp(url) {
  if (!url || typeof document === "undefined") return false;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  } catch (err) {
    try {
      window.location.href = url;
      return true;
    } catch (err2) {
      return false;
    }
  }
}

// Copy text to the clipboard, with an execCommand fallback for older web views.
// Used by the "Messages didn't open" fallback so the agent can still send the
// text by hand. Returns true on success.
export async function copyToClipboard(text) {
  const value = String(text ?? "");
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (err) {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, value.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    return false;
  }
}
